import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import { getRawAccessToken } from "Utils/authTokens";

export const VCMSPage = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const vcmsUrl =
    (window as unknown as { _env_?: Record<string, string> })._env_
      ?.REACT_APP_VCMS_URL ?? "/vcms-ui/";

  // Extract the sub-path after /vcms (e.g. /vcms/vehicles → /vehicles)
  const subPath = location.pathname.replace(/^\/vcms/, "") || "/";

  // Bake the requested path into the iframe's initial src (as a hash fragment)
  // instead of relying solely on the postMessage handshake to correct VCMS's
  // own default-route redirect after the fact. VCMS boots with an empty hash
  // and its router redirects to /vehicles synchronously on first paint — well
  // before any vcms:ready/vcms:goto round-trip could land — so without this,
  // the user sees Vehicles instead of the scope they actually picked
  // (DEV-5013). Captured once via ref: only the very first mount's path
  // should ever affect the iframe's src; later navigation is handled by the
  // vcms:goto postMessage effect below, not by recreating the iframe.
  const initialSrcRef = useRef<string>();
  if (initialSrcRef.current === undefined) {
    initialSrcRef.current = `${vcmsUrl}#${subPath}`;
  }

  // On FMS path change, tell the already-loaded iframe to navigate
  const iframeLoaded = useRef(false);
  useEffect(() => {
    if (!iframeLoaded.current) return; // wait until iframe is ready
    iframeRef.current?.contentWindow?.postMessage(
      { type: "vcms:goto", path: subPath },
      "*"
    );
  }, [subPath]);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      // VCMS navigated internally → sync the FMS URL
      if (e.data?.type === "vcms:navigate") {
        const target = "/vcms" + (e.data.path ?? "");
        if (target !== location.pathname) navigate(target, { replace: true });
      }
      // Resize the iframe to fit content
      if (e.data?.type === "vcms:resize" && iframeRef.current) {
        iframeRef.current.style.height = e.data.height + "px";
      }
      // Iframe finished loading — send initial path + JWT
      if (e.data?.type === "vcms:ready") {
        iframeLoaded.current = true;
        iframeRef.current?.contentWindow?.postMessage(
          { type: "vcms:goto", path: subPath },
          "*"
        );
        // Pass the Cognito access token so VCMS can identify the user in audit trails (it is
        // what the legacy `JWT` entry held for an SSO session); VCMS api.js adds the prefix.
        const token = getRawAccessToken();
        if (token) {
          iframeRef.current?.contentWindow?.postMessage(
            { type: "vcms:set-jwt", token },
            "*"
          );
        }
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [location.pathname, navigate, subPath]);

  return (
    <Box sx={{ width: "100%", height: "100vh" }}>
      <iframe
        ref={iframeRef}
        src={initialSrcRef.current}
        style={{ width: "100%", height: "100%", border: "none" }}
        title="VCMS"
      />
    </Box>
  );
};
